import React, { useState } from "react";
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const CustomTooltip = ({ data, x, y, visible }) => {
  if (!visible || !data) return null;

  const calculatePercentage = (current, previous) => {
    if (previous === 0) return { percent: "0", isPositive: true };
    const percent = (((current - previous) / previous) * 100).toFixed(1);
    const value = parseFloat(percent);
    return {
      percent: Math.abs(value).toFixed(1),
      isPositive: value >= 0,
      symbol: value >= 0 ? "+" : "-",
    };
  };

  const comparison = data.current !== undefined && data.previous !== undefined
    ? calculatePercentage(data.current, data.previous)
    : null;

  return (
    <div
      className="chart-tooltip"
      style={{
        left: `${x + 10}px`,
        top: `${y - 10}px`,
        position: "fixed",
        pointerEvents: "none",
        zIndex: 1000,
        backgroundColor: "white"
      }}
    >
      <div className="chart-tooltip__header">
        <p className="chart-tooltip__title">{data.month}</p>
      </div>
      <div className="chart-tooltip__content">
        {data.current !== undefined && (
          <div className="chart-tooltip__entry">
            <span className="chart-tooltip__label">2026 :</span>
            <span className="chart-tooltip__value chart-tooltip__value--primary">
              {typeof data.current === "number"
                ? data.current.toFixed(0)
                : data.current}
            </span>
          </div>
        )}
        {data.previous !== undefined && (
          <div className="chart-tooltip__entry">
            <span className="chart-tooltip__label">2025:</span>
            <span className="chart-tooltip__value chart-tooltip__value--secondary">
              {typeof data.previous === "number"
                ? data.previous.toFixed(0)
                : data.previous}
            </span>
          </div>
        )}
      </div>
      {comparison && (
        <div className="chart-tooltip__comparison">
          <span className="comparison-year-label">2025 vs 2026:</span>
          <span
            className={`comparison-text ${comparison.isPositive ? "positive" : "negative"}`}
          >
            {comparison.symbol} {comparison.percent}%
          </span>
        </div>
      )}
    </div>
  );
};

const ChartVisualization = ({
  data,
  chartType,
  tooltip,
  onBarMouseEnter,
  onBarMouseLeave,
  interval = "Daily",
}) => {
  const chartTypePrefix = chartType.split("-")[0];

  const values = data.map((item) => {
    const typePrefix = chartType.split("-")[1];
    switch (typePrefix) {
      case "monthly":
        return item.monthly;
      case "yearly":
        return item.yearly;
      default:
        return item.total;
    }
  });

  const maxValue = Math.max(...values);

  // Calculate dynamic Y-axis domain with padding
  const yAxisPadding = maxValue * 0.1;
  const yAxisMax = Math.ceil((maxValue + yAxisPadding) / 10) * 10;

  // Determine x-axis tick interval based on data length and interval
  const getXAxisTickInterval = () => {
    const dataLength = data.length;
    if (interval === "Yearly") return Math.max(0, Math.floor(dataLength / 4));
    if (interval === "Monthly") return Math.max(0, Math.floor(dataLength / 6)); // Show ~6 labels for monthly
    if (interval === "Weekly") return Math.max(0, Math.floor(dataLength / 8)); // Show ~8 labels for weekly
    return 0; // Show all for Daily
  };

  const getLegendInfo = () => {
    const typePrefix = chartType.split("-")[1];
    switch (typePrefix) {
      case "monthly":
        return { label: "monthly", color: "#3b82f6" };
      case "yearly":
        return { label: "yearly", color: "#f59e0b" };
      default:
        return { label: "total", color: "#3b82f6" };
    }
  };

  const legendInfo = getLegendInfo();

  // Render Bar Chart using Recharts
  const renderBarChart = () => {
    const typePrefix = chartType.split("-")[1];
    const isAllMetrics = typePrefix === "all";
    const xAxisTickInterval = getXAxisTickInterval();

    return (
      <div className="bar-chart-container">
        <ResponsiveContainer width="100%" height={320}>
          <BarChart
            data={data}
            margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="month"
              stroke="#6b7280"
              style={{ fontSize: "12px" }}
              interval={xAxisTickInterval}
            />
            <YAxis stroke="#6b7280" style={{ fontSize: "12px" }} domain={[0, yAxisMax]} />
            <Tooltip
              contentStyle={{
                backgroundColor: "#ffffff",
                border: "2px solid #3b82f6",
                borderRadius: "8px",
              }}
            />
            {isAllMetrics ? (
              <>
                <Legend />
                <Bar dataKey="total" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Jobs posted" />
                <Bar dataKey="client" fill="#0d9488" radius={[4, 4, 0, 0]} name="Bookings made" />
                <Bar dataKey="driver" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="Jobs completed" />
              </>
            ) : (
              <>
                <Bar
                  dataKey={typePrefix === "clients" ? "client" : typePrefix === "drivers" ? "driver" : "total"}
                  fill={legendInfo.color}
                  radius={[4, 4, 0, 0]}
                />
              </>
            )}
          </BarChart>
        </ResponsiveContainer>
        {!isAllMetrics && (
          <div className="chart-legend">
            <div className="legend-item">
              <div
                className="legend-color"
                style={{ backgroundColor: legendInfo.color }}
              />
              <span>{legendInfo.label}</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render Area Chart using Recharts
  const renderAreaChart = () => {
    const typePrefix = chartType.split("-")[1];
    const isAllMetrics = typePrefix === "all";
    const xAxisTickInterval = getXAxisTickInterval();

    return (
      <div className="chart-box-wrapper">
        <div className="area-chart-container">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={data}
              margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
            >
              <defs>
                {isAllMetrics ? (
                  <>
                    <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorClient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0d9488" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#0d9488" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorDriver" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                    </linearGradient>
                  </>
                ) : (
                  <linearGradient id="colorArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={legendInfo.color} stopOpacity={0.8} />
                    <stop offset="95%" stopColor={legendInfo.color} stopOpacity={0} />
                  </linearGradient>
                )}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="month"
                stroke="#6b7280"
                style={{ fontSize: "12px" }}
                interval={xAxisTickInterval}
              />
              <YAxis stroke="#6b7280" style={{ fontSize: "12px" }} domain={[0, yAxisMax]} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#ffffff",
                  border: "2px solid #3b82f6",
                  borderRadius: "8px",
                }}
              />
              {isAllMetrics ? (
                <>
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="total"
                    stroke="#3b82f6"
                    strokeWidth={2.5}
                    fillOpacity={1}
                    fill="url(#colorTotal)"
                    name="Jobs posted"
                  />
                  <Area
                    type="monotone"
                    dataKey="client"
                    stroke="#0d9488"
                    strokeWidth={2.5}
                    fillOpacity={1}
                    fill="url(#colorClient)"
                    name="Bookings made"
                  />
                  <Area
                    type="monotone"
                    dataKey="driver"
                    stroke="#8b5cf6"
                    strokeWidth={2.5}
                    fillOpacity={1}
                    fill="url(#colorDriver)"
                    name="Jobs completed"
                  />
                </>
              ) : (
                <Area
                  type="monotone"
                  dataKey={typePrefix === "clients" ? "client" : typePrefix === "drivers" ? "driver" : "total"}
                  stroke={legendInfo.color}
                  strokeWidth={2.5}
                  fillOpacity={1}
                  fill="url(#colorArea)"
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
          {!isAllMetrics && (
            <div className="chart-legend">
              <div className="legend-item">
                <div
                  className="legend-color"
                  style={{ backgroundColor: legendInfo.color }}
                />
                <span>{legendInfo.label}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render Line Chart using Recharts
  const renderLineChart = () => {
    const typePrefix = chartType.split("-")[1];
    const isAllMetrics = typePrefix === "all";
    const xAxisTickInterval = getXAxisTickInterval();

    return (
      <div className="chart-box-wrapper">
        <div className="line-chart-container">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={data}
              margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="month"
                stroke="#6b7280"
                style={{ fontSize: "12px" }}
                interval={xAxisTickInterval}
              />
              <YAxis stroke="#6b7280" style={{ fontSize: "12px" }} domain={[0, yAxisMax]} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#ffffff",
                  border: "2px solid #3b82f6",
                  borderRadius: "8px",
                }}
              />
              {isAllMetrics ? (
                <>
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="total"
                    stroke="#3b82f6"
                    strokeWidth={2.5}
                    dot={{ fill: "#3b82f6", r: 3.5 }}
                    activeDot={{ r: 5 }}
                    name="Jobs posted"
                  />
                  <Line
                    type="monotone"
                    dataKey="client"
                    stroke="#0d9488"
                    strokeWidth={2.5}
                    dot={{ fill: "#0d9488", r: 3.5 }}
                    activeDot={{ r: 5 }}
                    name="Bookings made"
                  />
                  <Line
                    type="monotone"
                    dataKey="driver"
                    stroke="#8b5cf6"
                    strokeWidth={2.5}
                    dot={{ fill: "#8b5cf6", r: 3.5 }}
                    activeDot={{ r: 5 }}
                    name="Jobs completed"
                  />
                </>
              ) : (
                <>
                  <Line
                    type="monotone"
                    dataKey={typePrefix === "clients" ? "client" : typePrefix === "drivers" ? "driver" : "total"}
                    stroke={legendInfo.color}
                    strokeWidth={2.5}
                    dot={{ fill: legendInfo.color, r: 3.5 }}
                    activeDot={{ r: 5 }}
                  />
                </>
              )}
            </LineChart>
          </ResponsiveContainer>
          {!isAllMetrics && (
            <div className="chart-legend">
              <div className="legend-item">
                <div
                  className="legend-color"
                  style={{ backgroundColor: legendInfo.color }}
                />
                <span>{legendInfo.label}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  switch (chartTypePrefix) {
    case "area":
      return renderAreaChart();
    case "line":
      return renderLineChart();
    default:
      return renderBarChart();
  }
};

export default ChartVisualization;
